import { useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, Cell, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import api from "../../../lib/api";

const PIE_COLORS = ["#FF5A1F", "#1D4ED8", "#10B981", "#F59E0B", "#8B5CF6"];

function Stat({ label, value, testid, accent }) {
  return (
    <div className="rounded-xl border border-adminborder bg-admincard p-4" data-testid={testid}>
      <p className="text-xs text-slate-400">{label}</p>
      <p className={`mt-1 font-heading text-2xl font-extrabold ${accent ? "text-saffron" : "text-white"}`}>{value}</p>
    </div>
  );
}

export default function AiDashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/ai/overview").then((r) => setData(r.data)).catch(() => {}); }, []);

  if (!data) return <div className="p-8 text-slate-400" data-testid="ai-dashboard-loading">Loading AI analytics…</div>;
  const t = data.totals;

  return (
    <div className="space-y-6 p-4 sm:p-6" data-testid="ai-dashboard">
      <div>
        <h1 className="font-heading text-xl font-bold text-white">AI Assistant</h1>
        <p className="text-sm text-slate-400">24×7 virtual customer-care executive — conversations, leads and conversions.</p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Stat label="Total Conversations" value={t.conversations} testid="ai-stat-conversations" accent />
        <Stat label="Today's Conversations" value={t.today} testid="ai-stat-today" />
        <Stat label="Qualified Leads" value={`${t.leads} (${t.hot_leads} hot)`} testid="ai-stat-leads" accent />
        <Stat label="Pickup Requests" value={t.pickups} testid="ai-stat-pickups" />
        <Stat label="Support Tickets" value={t.tickets} testid="ai-stat-tickets" />
        <Stat label="Human Handovers" value={t.handovers} testid="ai-stat-handovers" />
        <Stat label="Rate Enquiries" value={t.rate_requests} testid="ai-stat-rates" />
        <Stat label="Tracking Enquiries" value={t.track_requests} testid="ai-stat-tracks" />
        <Stat label="Chat Opens" value={t.chat_opens} testid="ai-stat-opens" />
        <Stat label="AI → Pickup Conversion" value={`${t.conversion_rate}%`} testid="ai-stat-conversion" accent />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-adminborder bg-admincard p-4" data-testid="ai-chart-conversations">
          <p className="mb-3 text-sm font-semibold text-white">Conversations by Day (14d)</p>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={data.conversations_by_day}>
              <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
              <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#64748b" }} tickFormatter={(d) => d.slice(5)} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#64748b" }} width={25} />
              <Tooltip contentStyle={{ background: "#111A3A", border: "1px solid #1e293b", fontSize: 12 }} />
              <Line type="monotone" dataKey="count" stroke="#FF5A1F" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-xl border border-adminborder bg-admincard p-4" data-testid="ai-chart-leads">
          <p className="mb-3 text-sm font-semibold text-white">Leads by Day (14d)</p>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data.leads_by_day}>
              <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
              <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#64748b" }} tickFormatter={(d) => d.slice(5)} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#64748b" }} width={25} />
              <Tooltip contentStyle={{ background: "#111A3A", border: "1px solid #1e293b", fontSize: 12 }} />
              <Bar dataKey="count" fill="#1D4ED8" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-xl border border-adminborder bg-admincard p-4" data-testid="ai-chart-languages">
          <p className="mb-3 text-sm font-semibold text-white">Language Distribution</p>
          {data.languages.length === 0
            ? <p className="text-xs text-slate-500">No data yet.</p>
            : <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={data.languages} dataKey="count" nameKey="name" innerRadius={45} outerRadius={75} label={{ fontSize: 11, fill: "#94a3b8" }}>
                    {data.languages.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#111A3A", border: "1px solid #1e293b", fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>}
        </div>
        <div className="grid gap-4">
          <div className="rounded-xl border border-adminborder bg-admincard p-4" data-testid="ai-top-destinations">
            <p className="mb-2 text-sm font-semibold text-white">Top Destination Countries</p>
            {data.top_destinations.length === 0 && <p className="text-xs text-slate-500">No data yet.</p>}
            {data.top_destinations.map((d) => (
              <div key={d.name} className="flex items-center justify-between border-b border-adminborder py-1.5 text-sm last:border-0">
                <span className="text-slate-300">{d.name}</span><span className="font-bold text-saffron">{d.count}</span>
              </div>
            ))}
          </div>
          <div className="rounded-xl border border-adminborder bg-admincard p-4" data-testid="ai-enquiry-types">
            <p className="mb-2 text-sm font-semibold text-white">Top Enquiry Types</p>
            {data.enquiry_types.length === 0 && <p className="text-xs text-slate-500">No data yet.</p>}
            {data.enquiry_types.map((d) => (
              <div key={d.name} className="flex items-center justify-between border-b border-adminborder py-1.5 text-sm last:border-0">
                <span className="text-slate-300">{d.name.replace(/_/g, " ")}</span><span className="font-bold text-express">{d.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
